import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Heart, Users, Calendar, Euro, FileText, Home, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { serviceJsonLd, faqJsonLd, filArianeJsonLd } from "../utils/donneesStructurees";
import { faqMaPrimeAdapt } from "../utils/faqMaPrimeAdapt";
import { ANNEE_BAREME, SOURCE_BAREME, plafond, formaterEuros } from "../utils/plafonds";
import GuidesLies from "../components/GuidesLies";
import LienAidesComplementaires from "../components/LienAidesComplementaires";

const MaPrimeAdapt = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const eligibilityConditions = [
    {
      title: "Âge et situation",
      items: [
        "70 ans et plus (sans condition)",
        "60-69 ans avec perte d'autonomie (GIR 1-6)", 
        "Moins de 60 ans en situation de handicap"
      ]
    },
    {
      title: "Type de logement", 
      items: [
        "Résidence principale uniquement",
        "Propriétaire occupant",
        "Locataire du parc privé (avec accord propriétaire)"
      ]
    },
    {
      title: "Conditions de revenus",
      items: [
        "Revenus modestes ou très modestes",
        "Selon les barèmes ANAH en vigueur",
        "Tous les membres du foyer comptabilisés"
      ]
    }
  ];

  const aideMontants = [
    {
      category: "Revenus très modestes",
      taux: "70%",
      plafond: "22 000€",
      amoMax: "800€",
      description: "Pour les ménages aux revenus les plus faibles"
    },
    {
      category: "Revenus modestes", 
      taux: "50%",
      plafond: "22 000€", 
      amoMax: "600€",
      description: "Selon les plafonds de ressources ANAH"
    }
  ];

  const travauxEligibles = [
    "Adaptation de la salle de bain (douche sécurisée)",
    "Installation monte-escalier ou ascenseur privatif",
    "Aménagement des accès extérieurs (rampes)",
    "Élargissement des passages et ouvertures",
    "Adaptation des sanitaires (WC, lavabo)",
    "Éclairage renforcé et automatisé",
    "Revêtements antidérapants",
    "Barres d'appui et dispositifs de sécurité",
    "Automatisation des ouvrants",
    "Solutions domotiques d'assistance"
  ];

  const nouveautes2025 = [
    {
      title: "Un compte unique au 17 août 2026",
      description: "MaPrimeRénov', Mon Projet Anah et France Rénov' fusionnent dans un espace personnel unique sur france-renov.gouv.fr, sécurisé par FranceConnect+."
    },
    {
      title: "Plateformes fermées du 3 au 17 août",
      description: "Aucune création de compte ni dépôt de dossier n'est possible pendant la migration. Nous préparons les dossiers en amont pour déposer dès la réouverture."
    },
    {
      title: "Aides et plafonds inchangés",
      description: "Les montants, les plafonds de ressources et les critères d'éligibilité ne sont pas modifiés par cette réforme."
    },
    {
      title: "Plafond de travaux maintenu",
      description: "Les travaux retenus restent plafonnés à 22 000 € HT, soit une aide maximale de 15 400 € à 70 %."
    }
  ];

  // Exemples chiffrés : le reste à charge est ce que les visiteurs cherchent
  // en priorité. Montants indicatifs, hors aides locales et caisses de retraite.
  const exemplesChiffres = [
    {
      titre: "Salle de bain adaptée",
      detail: "Remplacement d'une baignoire par une douche de plain-pied, sol antidérapant et barres d'appui",
      devis: "8 400 €",
      taux: "70 %",
      aide: "5 880 €",
      reste: "2 520 €",
      profil: "Ménage aux ressources très modestes"
    },
    {
      titre: "Monte-escalier droit",
      detail: "Fourniture et pose d'un monte-escalier sur volée droite, avec mise en sécurité de la cage d'escalier",
      devis: "6 200 €",
      taux: "50 %",
      aide: "3 100 €",
      reste: "3 100 €",
      profil: "Ménage aux ressources modestes"
    }
  ];

  const processus = [
    {
      step: "1",
      title: "Test d'éligibilité",
      description: "Vérification de votre éligibilité en ligne en 3 minutes",
      duree: "Immédiat"
    },
    {
      step: "2", 
      title: "Diagnostic à domicile",
      description: "Visite de notre visiteur spécialiste pour le diagnostic autonomie liberté - Visite Ergothérapeute si nécessaire",
      duree: "Sous 72 heures"
    },
    {
      step: "3",
      title: "Constitution du dossier",
      description: "Montage complet de votre dossier MaPrimeAdapt' par notre AMO",
      duree: "Sous 1 semaine"
    },
    {
      step: "4",
      title: "Instruction ANAH",
      description: "Traitement de votre demande par l'Agence Nationale de l'Habitat",
      duree: "2-3 mois"
    },
    {
      step: "5",
      title: "Réalisation des travaux",
      description: "Exécution par nos artisans labellisés avec suivi qualité",
      duree: "Selon projet"
    },
    {
      step: "6",
      title: "Versement des aides",
      description: "Paiement des subventions sur votre compte après achèvement",
      duree: "2-4 semaines"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="MaPrimeAdapt' 2026 Île-de-France | Simulez votre aide en 2 min | TEF AMO agréé"
        description="Calculez votre aide MaPrimeAdapt' gratuitement. Jusqu'à 15 400 € pour adapter votre logement. TEF, AMO agréé ANAH en IDF — accompagnement complet de A à Z."
        chemin="/maprimeadapt"
        donnees={[
          serviceJsonLd({
            nom: "Accompagnement AMO MaPrimeAdapt'",
            description: "Montage et suivi du dossier MaPrimeAdapt' auprès de l'ANAH, de la visite d'évaluation au versement des aides.",
            url: "/maprimeadapt",
          }),
          faqJsonLd(faqMaPrimeAdapt),
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "MaPrimeAdapt'", url: "/maprimeadapt" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  <Heart className="h-4 w-4 mr-2" />
                  MaPrimeAdapt' 2025-2026 • AMO Agréé ANAH
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  MaPrimeAdapt'{" "}
                  <span className="text-orange-700 block mt-2">L'aide qui change tout</span>
                </h1>
                
                <p className="text-xl text-gray-700 leading-relaxed">
                  L'aide financière de l'État pour adapter votre logement au vieillissement et au handicap. 
                  <strong className="text-orange-700"> Jusqu'à 15 400€ de subvention</strong> pour des travaux d'accessibilité plafonnés à 22 000€.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/simulateur">
                  <Button size="lg" className="bg-orange-700 hover:bg-orange-800 text-white shadow-lg group w-full sm:w-auto">
                    Tester mon éligibilité
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
               
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                  onClick={() => window.location.href = 'tel:0939035444'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  09 39 03 54 44
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">680 000</div>
                  <div className="text-sm text-gray-700">Objectif 2032</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">1,5 Md€</div>
                  <div className="text-sm text-gray-700">Budget 2025-2027</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-700">100%</div>
                  <div className="text-sm text-gray-700">AMO pris en charge</div>
                </div>
              </div>
            </div>

            {/* Info Box 2025 */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <Badge className="bg-orange-100 text-orange-800 border-orange-200 mb-4">
                  <Calendar className="h-3 w-3 mr-1" />
                  Nouveautés 2025-2026
                </Badge>
                <h2 className="text-xl font-semibold text-gray-900">
                  Évolutions de MaPrimeAdapt'
                </h2>
              </div>
              
              <div className="space-y-4">
                {nouveautes2025.map((nouveaute, index) => (
                  <div key={index} className="border-l-4 border-orange-300 pl-4">
                    <h3 className="font-semibold text-gray-900 text-sm">{nouveaute.title}</h3>
                    <p className="text-gray-700 text-sm">{nouveaute.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>À retenir :</strong> si votre dossier n'est pas encore déposé, rien n'est perdu.
                  Nous le montons pendant la fermeture des plateformes pour le transmettre dès le 17 août.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MaPrimeAdapt' Visual officiel */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              MaPrimeAdapt' : L&apos;aide qui transforme votre quotidien
            </h2>
          </div>
          
          {/* Image officielle MaPrimeAdapt' - Réduite */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-2xl p-4 shadow-xl">
              <img 
                src="/images/maprimeadapt.webp"
                width="778"
                height="613"
                loading="lazy" 
                alt="MaPrimeAdapt - Ma vie change, mon logement s adapte"
                className="w-full max-w-lg mx-auto rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Grid des travaux éligibles avec style cohérent site */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Salle de bain adaptée */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="relative">
                <img 
                  src="/images/salle-bain-pmr-vignette.webp"
                  alt="Salle de bain adaptée PMR"
                  width="640"
                  height="320"
                  loading="lazy"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute top-3 left-3">
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Jusqu'à 70%
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Salle de bain sécurisée</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Douche de plain-pied, barres d'appui, WC rehaussé, sol antidérapant
                </p>
              </div>
            </div>

            {/* Monte-escalier */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="relative">
                <img 
                  src="/images/wc-sureleve.webp"
                  width="256"
                  height="197"
                  loading="lazy" 
                  alt="Monte-escalier PMR"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute top-3 left-3">
                  <div className="bg-green-700 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Éligible
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Monte-escalier</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Fauteuils élévateurs, plateformes, ascenseurs privatifs
                </p>
                <Link
                  to="/monte-escalier"
                  className="inline-block mt-3 text-sm text-orange-700 underline hover:text-orange-800"
                >
                  Prix et aides du monte-escalier
                </Link>
              </div>
            </div>

            {/* Volets roulants */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="relative">
                <img 
                  src="/images/volets-automatiques.webp"
                  width="1024"
                  height="683"
                  loading="lazy" 
                  alt="Volets roulants électriques"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute top-3 left-3">
                  <div className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Confort
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Volets électriques</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Automatisation pour simplifier le quotidien et éviter les chutes
                </p>
              </div>
            </div>

            {/* Rampes et accès */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="relative">
                <img 
                  src="/images/eclairage.webp"
                  width="650"
                  height="500"
                  loading="lazy" 
                  alt="Rampe d'accès PMR"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute top-3 left-3">
                  <div className="bg-orange-700 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Accès
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Rampes et cheminements</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Accès sécurisés, élargissement portes, éclairage automatique
                </p>
              </div>
            </div>
          </div>

          {/* Informations officielles ANAH */}
          <div className="bg-gradient-to-r from-orange-100 to-blue-100 rounded-xl p-8 text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <div className="text-orange-700 font-bold text-2xl mr-4">MaPrimeAdapt'</div>
              <div className="text-gray-400">•</div>
              <div className="text-gray-700 ml-4 font-medium">Agence nationale de l'habitat (ANAH)</div>
            </div>
            <p className="text-lg text-gray-700 mb-4 italic">
              "Ma vie change, mon logement s'adapte"
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-2xl font-bold text-orange-700">680 000</div>
                <div className="text-sm text-gray-700">logements à adapter d'ici 2032</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-700">1,5 Md€</div>
                <div className="text-sm text-gray-700">budget 2025-2027</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-700">Jusqu'à 70%</div>
                <div className="text-sm text-gray-700">de prise en charge</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Info Section like Logiadapt */}
      <section className="py-24 bg-orange-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                MaPrimeAdapt' : c'est quoi exactement ?
              </h2>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  MaPrimeAdapt' ou <em>MaPrimeAdapt'</em> est une aide financière de l'État gérée par l'<strong>Agence nationale de l'habitat (Anah)</strong> 
                  permettant de réaliser des travaux d'adaptation du logement réservée aux personnes âgées ou en situation de handicap.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Cette subvention vise à financer des travaux d'aménagements permettant d'améliorer l'accessibilité et la sécurité de votre domicile 
                  pour favoriser le maintien à domicile et l'autonomie.
                </p>
                <div className="bg-white p-6 rounded-lg border border-orange-200">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-orange-700" />
                    Mesure du Conseil National de la Refondation (CNR)
                  </h3>
                  <p className="text-sm text-gray-700">
                    Élaborée entre 2022 et 2023, MaPrimeAdapt' a été intégrée à la feuille de route gouvernementale 
                    pour rendre la France <em>"plus indépendante et plus juste"</em> ainsi que dans le cadre du 
                    Conseil National du Handicap (CNH) 2023.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Objectifs et budget MaPrimeAdapt'
              </h3>
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-700 mb-2">680 000</div>
                  <p className="text-gray-700 text-sm">logements adaptés visés d'ici 2032</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-700 mb-2">1,5 Md€</div>
                  <p className="text-gray-700 text-sm">budget alloué 2025-2027</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-700 mb-2">250 000</div>
                  <p className="text-gray-700 text-sm">logements adaptés d'ici 2027</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-orange-800">
                    <strong>Ambition :</strong> Doubler le nombre de logements adaptés chaque année 
                    par rapport aux dispositifs précédents
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Conditions d'éligibilité MaPrimeAdapt' 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Découvrez si vous pouvez bénéficier de cette aide exceptionnelle de l'État
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {eligibilityConditions.map((condition, index) => (
              <Card key={index} className="border-2 hover:border-orange-200 transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-xl text-orange-700 text-center">
                    {condition.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {condition.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Montants */}
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Montants MaPrimeAdapt' 2026
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {aideMontants.map((montant, index) => (
                <div key={index} className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{montant.category}</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Taux de prise en charge</span>
                      <span className="text-2xl font-bold text-orange-700">{montant.taux}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Plafond travaux</span>
                      <span className="text-xl font-semibold text-gray-900">{montant.plafond}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">AMO (gratuit)</span>
                      <span className="text-lg font-semibold text-green-700">{montant.amoMax}</span>
                    </div>
                    <p className="text-sm text-gray-700 pt-2">{montant.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Plafonds de revenus détaillés */}
            <div className="border-t pt-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Plafonds de revenus MaPrimeAdapt' {ANNEE_BAREME} (Île-de-France)
              </h4>
              {/* Chiffres lus dans utils/plafonds.js : ce tableau affichait un
                  barème 2025 écrit en dur, périmé depuis le 1er janvier 2026. */}
              <div className="overflow-x-auto" tabIndex="0" role="group" aria-label="Tableau des plafonds de revenus, défilement horizontal">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Plafonds de revenu fiscal de référence en Île-de-France pour
                    MaPrimeAdapt' en {ANNEE_BAREME}
                  </caption>
                  <thead>
                    <tr className="bg-orange-100">
                      <th scope="col" className="text-left p-3 font-semibold">Composition du ménage</th>
                      <th scope="col" className="text-center p-3 font-semibold text-blue-700">Revenus très modestes</th>
                      <th scope="col" className="text-center p-3 font-semibold text-green-800">Revenus modestes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <tr key={n} className={n % 2 === 0 ? "bg-gray-50" : undefined}>
                        <th scope="row" className="p-3 font-medium text-left">
                          {n} personne{n > 1 ? "s" : ""}
                        </th>
                        <td className="p-3 text-center text-blue-700">
                          ≤ {formaterEuros(plafond("tresModestes", n))}
                        </td>
                        <td className="p-3 text-center text-green-800">
                          ≤ {formaterEuros(plafond("modestes", n))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-700 mt-4 text-center">
                Revenu fiscal de référence de l&apos;année N-1, cumulé pour toutes les
                personnes qui occupent le logement. Barème en vigueur au 1<sup>er</sup> janvier {ANNEE_BAREME}.
                <br />
                Par personne supplémentaire au-delà de 5 :
                + {formaterEuros(plafond("tresModestes", 6) - plafond("tresModestes", 5))} (très modestes)
                {" / "}+ {formaterEuros(plafond("modestes", 6) - plafond("modestes", 5))} (modestes).{" "}
                <a
                  href={SOURCE_BAREME.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-orange-700"
                >
                  Voir le barème officiel
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples chiffrés de reste à charge */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Combien reste-t-il réellement à votre charge ?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Deux projets courants, avant cumul avec les aides de votre caisse de retraite
              et de votre commune, qui font encore baisser ces montants.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {exemplesChiffres.map((ex) => (
              <div key={ex.titre} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{ex.titre}</h3>
                <p className="text-sm text-gray-700 mb-6">{ex.detail}</p>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Devis des travaux</span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">{ex.devis}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-green-700">MaPrimeAdapt' ({ex.taux})</span>
                    <span className="font-semibold text-green-700 whitespace-nowrap">- {ex.aide}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3">
                    <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                    <span className="text-2xl font-bold text-gray-400 whitespace-nowrap line-through decoration-2 decoration-orange-400">{ex.reste}</span>
                  </div>
                </div>
                <LienAidesComplementaires />

                <p className="text-xs text-gray-700 mt-6">{ex.profil}. Montants indicatifs.</p>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-700 max-w-3xl mx-auto mt-10">
            Le taux appliqué dépend de vos ressources : 70 % pour les ménages très modestes,
            50 % pour les ménages modestes. Les travaux retenus sont plafonnés à 22 000 € HT,
            soit une aide maximale de 15 400 €.
          </p>
        </div>
      </section>

      {/* Travaux Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Travaux pris en charge par MaPrimeAdapt'
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {travauxEligibles.map((travail, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 text-sm">{travail}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-orange-50 rounded-lg border border-orange-200">
                <h3 className="font-semibold text-gray-900 mb-3">
                  <FileText className="inline h-5 w-5 mr-2" />
                  À retenir pour 2025-2026
                </h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Liste des travaux éligibles élargie en continu</li>
                  <li>• Solutions domotiques de plus en plus prises en charge</li>
                  <li>• Possibilité de travaux préventifs pour l'anticipation</li>
                  <li>• Extension possible aux équipements connectés en 2026</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Exemple concret 2026
              </h3>
              
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Profil</h4>
                  <p className="text-sm text-gray-700">Mme Durand, 74 ans, revenus très modestes, Paris</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Projet adapté</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Remplacement baignoire par douche : 15 000€</li>
                    <li>• AMO diagnostic + suivi : 800€</li>
                    <li className="font-semibold pt-2 border-t">Total projet : 15 800€</li>
                  </ul>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Financement</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">MaPrimeAdapt' (70%)</span>
                      <span className="text-green-700 font-semibold">- 10 500€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Aide Ville de Paris</span>
                      <span className="text-green-700 font-semibold">- 3 000€</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">Reste à charge</span>
                      <span className="text-orange-700 font-bold">2 300€</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Processus MaPrimeAdapt' 2026
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Un accompagnement simplifié pour obtenir votre aide en 6 étapes claires
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processus.map((etape, index) => (
              <Card key={index} className="border-2 hover:border-orange-200 transition-all duration-300">
                <CardHeader className="text-center">
                  <div className="w-12 h-12 bg-orange-700 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                    {etape.step}
                  </div>
                  <CardTitle className="text-lg text-gray-900">
                    {etape.title}
                  </CardTitle>
                  <CardDescription className="text-orange-700 font-medium">
                    {etape.duree}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm text-center">{etape.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-white rounded-lg p-6 max-w-2xl mx-auto shadow-lg">
              <h3 className="font-semibold text-gray-900 mb-3">
                Délai total estimé 2026
              </h3>
              <p className="text-lg text-orange-700 font-bold">4 à 6 mois</p>
              <p className="text-sm text-gray-700 mt-2">
                De la simulation à la réception des aides (objectif de réduction à 3-4 mois en 2026)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ MaPrimeAdapt' Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              MaPrimeAdapt' : La foire aux questions
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Toutes les réponses aux questions les plus fréquentes sur MaPrimeAdapt' 2026
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
              <h3 className="font-semibold text-gray-900 mb-3">
                Dans quelles conditions puis-je bénéficier de l'aide MaPrimeAdapt' ?
              </h3>
              <div className="text-gray-700 space-y-3 text-sm">
                <p>
                  Les <strong>seniors de plus de 70 ans</strong> sont éligibles à MaPrimeAdapt' sans condition de perte d'autonomie. 
                  Pour les <strong>personnes âgées de 60 à 69 ans</strong>, il faudra fournir un justificatif de <strong>GIR 1 à 6</strong>. 
                  Enfin, les personnes de moins de 60 ans peuvent aussi bénéficier de l'aide financière s'ils sont en <strong>situation de handicap</strong>, 
                  justifiant d'au moins <strong>50% d'invalidité</strong>. La PCH, l'AAH, l'AEEH ou la carte d'invalidité vous seront demandées 
                  pour statuer de votre perte d'autonomie.
                </p>
                <p>
                  Il est à noter que seules les <strong>résidences principales</strong> sont éligibles à l'aide MaPrimeAdapt'. 
                  Pour que les travaux soient pris en charge, vous pouvez être <strong>propriétaire occupant</strong> de votre logement ou 
                  <strong>locataire du parc privé</strong> avec accord du propriétaire. Les logements sociaux ne sont pas éligibles à MaPrimeAdapt'.
                </p>
                <p>
                  Concernant les conditions de revenus, l'ANAH a déterminé des <strong>grilles de revenus</strong> qui placent les demandeurs 
                  dans différentes catégories. Pour bénéficier de MaPrimeAdapt', vous devez avoir des <strong>ressources modestes</strong> ou 
                  <strong>très modestes</strong>. Cela dépend de votre région de résidence et du nombre de personnes vivant à votre domicile. 
                  Attention, l'ANAH considère les revenus de tous les membres du foyer, même les <strong>habitants à titre gratuit</strong>.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-3">
                À quoi sert le passage d'un visiteur spécialiste ?
              </h3>
              <div className="text-gray-700 space-y-3 text-sm">
                <p>
                  Dans le cadre de l'accompagnement MaPrimeAdapt', un <strong>visiteur spécialiste</strong> passera à votre domicile pour réaliser 
                  un <strong>diagnostic autonomie liberté</strong>. Ce professionnel évalue vos besoins d'adaptation et les obstacles présents 
                  dans votre logement pour maintenir votre autonomie.
                </p>
                <p>
                  Le diagnostic autonomie liberté est un <strong>examen approfondi</strong> pendant lequel le visiteur s'attachera à identifier 
                  les difficultés que vous pourriez rencontrer pour réaliser vos activités quotidiennes comme <strong>se laver, aller à la toilette, 
                  préparer ses repas, se mouvoir</strong> dans votre logement. Il évaluera aussi l'environnement et la sécurité de votre domicile.
                </p>
                <p>
                  À la suite de sa visite, le visiteur spécialiste éditera un <strong>rapport détaillé</strong> identifiant les zones à adapter 
                  dans le domicile pour que vous conserviez votre <strong>indépendance</strong> en toute <strong>sécurité</strong> et gagniez en 
                  <strong>confort</strong> chez vous. Il proposera des <strong>solutions adaptées</strong> que nous prendrons en compte dans la 
                  conception du dossier et la réalisation des travaux. Si nécessaire, un <strong>ergothérapeute</strong> peut être envoyé en 
                  complément selon l'utilité du cas.
                </p>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-3">
                Quels sont le rôle et le coût de l'accompagnement de l'AMO ?
              </h3>
              <div className="text-gray-700 space-y-3 text-sm">
                <p>
                  <strong>L'AMO</strong> (assistant à maîtrise d'ouvrage) est un <strong>opérateur indépendant</strong> mandaté par 
                  <strong>l'ANAH</strong> et par <strong>France Rénov</strong> pour suivre le projet d'adaptation de votre logement de A à Z. 
                  Son accompagnement est obligatoire pour bénéficier de MaPrimeAdapt'.
                </p>
                <p>
                  Tout au long du projet, l'AMO sera votre contact privilégié. Il se charge de rédiger le <strong>dossier de demande de financement</strong>, 
                  il organise le <strong>passage d'un visiteur spécialiste</strong> à votre domicile et est responsable du <strong>suivi de vos travaux</strong>. 
                  L'accompagnement de l'AMO est <strong>pris en charge intégralement par l'ANAH</strong>. Vous devrez avancer les frais et serez remboursé 
                  à la fin du projet de l'intégralité de la somme avancée.
                </p>
                <div className="bg-white p-4 rounded border-l-4 border-green-500">
                  <p className="font-medium text-gray-900 mb-2">Prise en charge AMO 2026 :</p>
                  <ul className="space-y-1 text-sm">
                    <li>✓ <strong>800€</strong> pour les personnes âgées de <strong>plus de 80 ans</strong> ou ayant subi une <strong>chute récente</strong></li>
                    <li>✓ <strong>600€</strong> pour les autres bénéficiaires de MaPrimeAdapt'</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-300">
              <h3 className="font-semibold text-gray-900 mb-3">
                Peut-on cumuler l'aide MaPrimeAdapt' avec d'autres aides ?
              </h3>
              <div className="text-gray-700 space-y-3 text-sm">
                <p>
                  <strong>Oui</strong>, il est tout à fait possible de cumuler MaPrimeAdapt' avec d'autres aides financières, 
                  ce qui permet d'optimiser significativement le financement de vos travaux.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Aides cumulables :</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Aides des collectivités locales (région, département, commune)</li>
                      <li>• Aides des caisses de retraite (CARSAT, MSA, etc.)</li>
                      <li>• Autres aides de l'ANAH (MaPrimeRénov')</li>
                      <li>• Volet aides techniques de l'APA et PCH</li>
                      <li>• Action Logement, pour les salariés du privé</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-2">Bon à savoir :</p>
                    <ul className="space-y-1 text-xs">
                      {/* Le crédit d'impôt autonomie (25 %) est supprimé pour les
                          dépenses payées à compter du 1er janvier 2026 : on ne le
                          présente plus comme cumulable. */}
                      <li>• Le crédit d'impôt autonomie de 25 % ne s'applique plus aux dépenses payées depuis le 1er janvier 2026</li>
                      <li>• Les aides locales varient fortement d'un département à l'autre</li>
                      <li>• Le cumul se prépare avant le dépôt : certaines aides ne sont plus mobilisables une fois les travaux engagés</li>
                    </ul>
                  </div>
                </div>
                <p className="mt-4 pt-3 border-t border-gray-200">
                  <Link to="/aides-complementaires" className="text-sm font-semibold text-orange-700 underline hover:text-orange-800">
                    Nous recherchons les aides qui réduisent votre reste à charge après MaPrimeAdapt' →
                  </Link>
                </p>
              </div>
            </div>

            {/* Ces questions alimentent aussi le balisage FAQPage : le tableau
                faqMaPrimeAdapt est la seule source, affichage et données
                structurées ne peuvent donc pas diverger. */}
            <Accordion type="single" collapsible className="space-y-4 pt-4">
              {faqMaPrimeAdapt.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border border-gray-300 rounded-lg px-6 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-orange-700 py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Prêt à bénéficier de MaPrimeAdapt' ?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Testez votre éligibilité gratuitement et découvrez le montant d'aide auquel vous avez droit
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <Link to="/simulateur" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="bg-white text-orange-700 hover:bg-gray-100 w-full sm:w-auto shadow-lg group"
              >
                <Users className="mr-2 h-5 w-5" />
                Simulation immédiate
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-green-200 hover:bg-green-50 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
              

            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-orange-700 w-full sm:w-auto"
              onClick={() => window.location.href = 'tel:0939035444'}
            >
              <Phone className="mr-2 h-5 w-5" />
              09 39 03 54 44
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm">
              Service gratuit • AMO agréé ANAH • Accompagnement personnalisé
            </p>
          </div>
        </div>
      </section>

      <GuidesLies slugs={["comment-obtenir-maprimeadapt", "amo-obligatoire-maprimeadapt", "maprimeadapt-2026-ce-qui-change", "refus-maprimeadapt-motifs-recours"]} />

      </main>

      <Footer />
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />

    </div>
  );
};

export default MaPrimeAdapt;